import type { WidgetNodeProps } from "@/pages/canvas/types/canvasNodeData";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Bell,
  Calendar,
  Check,
  Clock,
  Flag,
  Folder,
  Heart,
  Info,
  Link,
  Lock,
  Pin,
  Plus,
  Star,
  Target,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Users,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { WidgetPreset } from "../types";

/**
 * ICON_ITEMS — Lucide marks placed AS CONTENT (tray-stickers.jsx): a palette
 * of pictures the user drops on a board, so this list creates no registry
 * pairings; where a mark already has a ruled concept it is the same Lucide
 * export. Twenty-four, three rows of eight, in the sheet's order. Stroke
 * 1.75, resizable. A bare mark with no field under it, so it takes the
 * theme foreground and moves with the mode, as the sheet's tiles draw it in
 * the tile's own text colour (ROUND5 §2, D47).
 */
export const ICON_STICKER_SIZE = 96;
const STROKE = 1.75;

const ICONS: { key: string; name: string; icon: LucideIcon }[] = [
  { key: "check", name: "Check", icon: Check },
  { key: "cross", name: "Cross", icon: X },
  { key: "arrow-up", name: "Arrow up", icon: ArrowUp },
  { key: "arrow-down", name: "Arrow down", icon: ArrowDown },
  { key: "arrow-left", name: "Arrow left", icon: ArrowLeft },
  { key: "arrow-right", name: "Arrow right", icon: ArrowRight },
  { key: "flag", name: "Flag", icon: Flag },
  { key: "pin", name: "Pin", icon: Pin },
  { key: "bell", name: "Bell", icon: Bell },
  { key: "lock", name: "Lock", icon: Lock },
  { key: "clock", name: "Clock", icon: Clock },
  { key: "target", name: "Target", icon: Target },
  { key: "zap", name: "Zap", icon: Zap },
  { key: "heart", name: "Heart", icon: Heart },
  { key: "star", name: "Star", icon: Star },
  { key: "warning", name: "Warning", icon: TriangleAlert },
  { key: "info", name: "Info", icon: Info },
  { key: "plus", name: "Plus", icon: Plus },
  { key: "trending-up", name: "Trending up", icon: TrendingUp },
  { key: "trending-down", name: "Trending down", icon: TrendingDown },
  { key: "users", name: "People", icon: Users },
  { key: "calendar", name: "Calendar", icon: Calendar },
  { key: "folder", name: "Folder", icon: Folder },
  { key: "link", name: "Link", icon: Link },
];

export const ICON_STICKER_BY_KEY = new Map(ICONS.map((i) => [i.key, i]));

export function IconStickerArt({
  name,
  title,
}: {
  name: string;
  title: string;
}) {
  const entry = ICON_STICKER_BY_KEY.get(name);
  if (!entry) return null;
  const Glyph = entry.icon;
  return (
    <Glyph
      size="100%"
      strokeWidth={STROKE}
      color="currentColor"
      aria-label={title}
      role="img"
      focusable="false"
    />
  );
}

/** The mark's ink on the ground and in the tray: the theme foreground. */
const ICON_INK = "hsl(var(--foreground))";

export function IconStickerWidget({ data }: WidgetNodeProps) {
  const d = data as { icon?: string; label?: string };
  return (
    <div
      style={{
        width: ICON_STICKER_SIZE,
        height: ICON_STICKER_SIZE,
        color: ICON_INK,
      }}
    >
      <IconStickerArt name={d.icon ?? ""} title={d.label ?? "Icon"} />
    </div>
  );
}

const preview = (key: string, name: string) =>
  function IconStickerPreview() {
    return (
      <div style={{ width: "100%", aspectRatio: "1", color: ICON_INK }}>
        <IconStickerArt name={key} title={name} />
      </div>
    );
  };

export const ICON_STICKER_PRESETS: WidgetPreset[] = ICONS.map((i) => ({
  id: `ds-icon-${i.key}`,
  label: i.name,
  family: "icon",
  component: IconStickerWidget,
  preview: preview(i.key, i.name),
  defaultWidth: ICON_STICKER_SIZE,
  defaultHeight: ICON_STICKER_SIZE,
  resizable: { minWidth: 24, minHeight: 24 },
  initialData: { icon: i.key },
}));
